"use client";

import { Button } from "@/components/ui/button";
import { ResponsiveModal } from "@west-tennessee-consulting/ui";
import { TagBadgeItem } from "./TagBadgeItem";
import { TagsAddInput } from "./TagsAddInput";
import type { TagEntityType } from "./useInlineTagsEditorState";

interface TagsEditorModalProps {
  tagsModalId: string;
  entityType: TagEntityType;
  parsedTags: string[];
  maxTags: number;
  isLoading: boolean;
  newTagInput: string;
  onInputChange: (value: string) => void;
  onKeyPress: (e: React.KeyboardEvent) => void;
  onAdd: () => void;
  onCancelAdd: () => void;
  onRemove: (tag: string) => void;
  onClose: () => void;
}

export function TagsEditorModal({
  tagsModalId,
  entityType,
  parsedTags,
  maxTags,
  isLoading,
  newTagInput,
  onInputChange,
  onKeyPress,
  onAdd,
  onCancelAdd,
  onRemove,
  onClose,
}: TagsEditorModalProps) {
  return (
    <ResponsiveModal
      globalModalId={tagsModalId}
      title="Edit Tags"
      description={`Manage tags for this ${entityType.slice(0, -1)}`}
      maxWidth="md"
      side="bottom"
    >
      <div className="space-y-4">
        <div>
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Current Tags ({parsedTags.length}/{maxTags})
          </h3>
          {parsedTags.length === 0 ? (
            <div className="text-sm text-gray-500 italic p-3 bg-gray-50 dark:bg-gray-900 rounded-md border border-dashed">
              No tags yet
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {parsedTags.map((tag) => (
                <TagBadgeItem key={tag} tag={tag} isLoading={isLoading} onRemove={onRemove} />
              ))}
            </div>
          )}
        </div>

        {parsedTags.length < maxTags && (
          <div>
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Add New Tag</h3>
            <div className="flex items-center gap-2">
              <TagsAddInput
                newTagInput={newTagInput}
                isLoading={isLoading}
                onInputChange={onInputChange}
                onKeyPress={onKeyPress}
                onAdd={onAdd}
                onCancel={onCancelAdd}
                inputClassName="flex-1"
                addButtonLabel="Add"
                showCancel={false}
              />
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            Close
          </Button>
        </div>
      </div>
    </ResponsiveModal>
  );
}
